import { useCallback, useEffect, useState } from 'react'

import { fetchCompanyBootstrap, loginToBackend, readMicrosoftTokenClaims, readSessionClaims, type SessionClaims } from '../api'
import { AccountManager } from '../authConfig'

import type { OfficeOption } from '../models/rooms'

const accountManager = new AccountManager()

function mapBootstrapOffices(
    bootstrap: Awaited<ReturnType<typeof fetchCompanyBootstrap>>,
): OfficeOption[] {
    return (bootstrap.company.Offices?.items || [])
        .filter((office): office is NonNullable<typeof office> => Boolean(office?.id && office.name))
        .map((office) => ({
            id: office.id,
            name: office.name,
            hours: `${office.workingFrom || '08:00'} - ${office.workingTo || '18:00'}`,
            timeZone: office.timeZone || 'Europe/Berlin',
        }))
}

export function useTaskpaneSession() {
    const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading')
    const [authMessage, setAuthMessage] = useState('Authorizing your Microsoft account...')
    const [sessionClaims, setSessionClaims] = useState<SessionClaims | null>(null)
    const [microsoftTenantId, setMicrosoftTenantId] = useState('')
    const [sessionToken, setSessionToken] = useState('')
    const [offices, setOffices] = useState<OfficeOption[]>([])
    const [initialOfficeId, setInitialOfficeId] = useState('')

    const authorizeUser = useCallback(async () => {
        setStatus('loading')
        setAuthMessage('Authorizing your Microsoft account...')

        try {
            await accountManager.initialize()

            setAuthMessage('Finishing secure sign-in...')
            const accessToken = await accountManager.ssoGetAccessToken([
                'api://4416b380-a25d-4182-b87a-87a94710822a/access_as_user',
            ])

            if (!accessToken) {
                throw new Error('No Microsoft access token was returned.')
            }

            const microsoftClaims = readMicrosoftTokenClaims(accessToken)

            setAuthMessage('Connecting to Yoffix...')
            await loginToBackend(accessToken)

            const token = sessionStorage.getItem('yoffixToken')
            if (!token) {
                throw new Error('Backend authorization did not return a Yoffix session token.')
            }

            const claims = readSessionClaims(token)

            setSessionToken(token)
            setSessionClaims(claims)
            setMicrosoftTenantId(microsoftClaims.tenantId)

            setAuthMessage('Loading offices and rooms...')
            const bootstrap = await fetchCompanyBootstrap(token, claims)

            const nextOffices = mapBootstrapOffices(bootstrap)

            if (!nextOffices.length) {
                throw new Error('No offices available for this company.')
            }

            const nextInitialOfficeId =
                bootstrap.employee.favouriteOfficeID || bootstrap.employee.officeID || nextOffices[0].id

            setOffices(nextOffices)
            setInitialOfficeId(
                nextOffices.some((office) => office.id === nextInitialOfficeId) ? nextInitialOfficeId : nextOffices[0].id,
            )
            setStatus('ready')
        } catch (error) {
            console.error(error)
            setStatus('error')
            setAuthMessage(error instanceof Error ? `Authorization failed. ${error.message}` : 'Authorization failed.')
        }
    }, [])

    useEffect(() => {
        void authorizeUser()
    }, [authorizeUser])

    return {
        status,
        authMessage,
        sessionClaims,
        microsoftTenantId,
        sessionToken,
        offices,
        initialOfficeId,
        retry: authorizeUser,
    }
}
